/**
 * @file        src/pages/seo/seoShared.jsx
 * @description Pieces every SEO Management screen shares: the brand picker
 *              (SEO is per STOREFRONT, so every screen asks which one — the
 *              choice rides in `?brand=` so a link from SEO Health lands on the
 *              same storefront), the "this site is noindex right now" banner,
 *              a debounce hook, the character counter, and the two previews.
 *
 *              THE PREVIEWS RENDER resolve() OUTPUT ONLY. GooglePreview and
 *              SocialPreview take the object POST /api/seo/preview returns —
 *              never form values — so what they show is exactly what the
 *              storefront will serve, fallbacks, truncation and title template
 *              included.
 * @author      Kinetika Labs
 * @created     2026-10-09
 * @changelog
 *   - 2026-10-09 Claude — Initial (SEO Management).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import BrandSelect from '../../components/BrandSelect';
import Chip from '../../components/Chip';

export const TITLE_LIMIT = 60;
export const DESCRIPTION_LIMIT = 160;

/** The storefront this screen edits, kept in the URL (`?brand=`). */
export function useSeoBrand() {
  const [params, setParams] = useSearchParams();
  const brand = params.get('brand') || '';
  const setBrand = useCallback((v) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v) next.set('brand', v); else next.delete('brand');
      return next;
    }, { replace: true });
  }, [setParams]);
  return [brand, setBrand];
}

/** Append the current brand to an SEO screen link. */
export const withBrand = (path, brand) => (brand && path.startsWith('/seo/') ? `${path}?brand=${encodeURIComponent(brand)}` : path);

export function BrandBar({ brand, onChange, children }) {
  return (
    <div className="seo-brandbar">
      <div className="field">
        <label>Storefront</label>
        <BrandSelect value={brand} onChange={onChange} placeholder="Select storefront" />
      </div>
      {children}
    </div>
  );
}

export function PickBrand() {
  return (
    <article className="panel glass">
      <div className="empty">SEO is configured per storefront — pick one above.</div>
    </article>
  );
}

/** Why the LIVE storefront is noindex right now, whatever any page says. */
export function NoindexBanner({ reason, brand }) {
  if (reason === 'kill-switch') {
    return (
      <div className="notice-banner danger">
        <span>
          <b>“Do not index this site” is ON</b> — every page of this storefront is served noindex and
          robots.txt answers <code>Disallow: /</code>. The previews below show the production view.
        </span>
        <Link to={withBrand('/seo/settings', brand)}>Global SEO →</Link>
      </div>
    );
  }
  if (reason === 'no-canonical-host') {
    return (
      <div className="notice-banner">
        <span>
          <b>Canonical host is not set</b> — until it is, the storefront serves every page noindex on
          every host. The previews below show what each page will say once it is set.
        </span>
        <Link to={withBrand('/seo/settings', brand)}>Set it in Global SEO →</Link>
      </div>
    );
  }
  return null;
}

export function useDebounced(value, ms = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Soft limit: over the limit is a warning colour, never a refusal. */
export function Counter({ value, limit }) {
  const n = (value || '').trim().length;
  return (
    <span className={`seo-counter${n > limit ? ' over' : ''}`}>
      {n}/{limit}{n > limit ? ' — will be cut in search results' : ''}
    </span>
  );
}

/** What an empty field resolves to (from the API's `fallbacks`). */
export function Fallback({ value, show }) {
  if (!show) return null;
  return (
    <div className="seo-fallback">
      empty → {value ? <b>{value}</b> : <i>nothing (left out of the page)</i>}
    </div>
  );
}

function displayUrl(canonical, path) {
  if (!canonical) return { site: 'Your storefront', url: `${path || '/'} — canonical host not set` };
  try {
    const u = new URL(canonical);
    const crumbs = u.pathname.split('/').filter(Boolean);
    return { site: u.host, url: `${u.origin}${crumbs.length ? ` › ${crumbs.join(' › ')}` : ''}` };
  } catch {
    return { site: canonical, url: canonical };
  }
}

/** A Google result, drawn from resolve() output. */
export function GooglePreview({ resolved, loading }) {
  if (!resolved) return <div className="serp"><span className="muted">{loading ? 'Rendering preview…' : 'No preview yet.'}</span></div>;
  const { site, url } = displayUrl(resolved.canonical, resolved.path);
  return (
    <div className="serp" aria-label="Google result preview">
      <div className="serp-site">{site}</div>
      <div className="serp-url">{url}</div>
      <div className="serp-title">{resolved.title}</div>
      <div className="serp-desc">{resolved.description || <i>No description — Google will pick text from the page.</i>}</div>
      <div className="serp-robots">
        <Chip tone={resolved.indexable ? 'ok' : 'rej'}>{resolved.robots}</Chip>
      </div>
    </div>
  );
}

/** A 1200×630 social card (Facebook / LinkedIn / WhatsApp shape), drawn from resolve() output. */
export function SocialPreview({ resolved }) {
  if (!resolved) return null;
  const og = resolved.og || {};
  let host = '';
  try { host = og.url ? new URL(og.url).host : ''; } catch { host = ''; }
  return (
    <div className="social-card" aria-label="Social share preview">
      <div
        className="social-card-img"
        style={og.image ? { backgroundImage: `url("${String(og.image).replace(/"/g, '%22')}")` } : undefined}
      >
        {og.image ? null : 'No image — shares as a bare link'}
      </div>
      <div className="social-card-body">
        <div className="social-card-host">{host || og.siteName}</div>
        <div className="social-card-title">{og.title}</div>
        {og.description ? <div className="social-card-desc">{og.description}</div> : null}
      </div>
    </div>
  );
}

export function SectionTitle({ children }) {
  return <div className="field full"><h4 className="form-section">{children}</h4></div>;
}

export function Field({ label, err, full, hint, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label}</label>
      {children}
      {hint && !err ? <div className="field-hint muted">{hint}</div> : null}
      {err ? <div className="field-error">{typeof err === 'string' ? err : JSON.stringify(err)}</div> : null}
    </div>
  );
}

export function errMessage(err, fallback = 'Something went wrong') {
  return err?.response?.data?.error?.message || fallback;
}

export function errFields(err) {
  return err?.response?.data?.error?.fields || {};
}
